import { RenderingSettingsContext } from "@/components/common/rendering-settings-context";
import { DEFAULT_LOOK_AT_DIR } from "@/components/r3f/animations/to-pano-animation";
import { SuspenseLoadNotifier } from "@/components/r3f/utils/suspense-load-notifier";
import { useCached3DObjectIfExists } from "@/object-cache";
import { useAppSelector } from "@/store/store-hooks";
import { selectBestCameraViewForElementInThreeSpace } from "@/utils/cam-view";
import { usePanoCameraTransform } from "@/utils/camera-transform";
import {
  DEFAULT_CANVAS_OPTIONS,
  SphereControls,
  type SphereControlsRef,
} from "@faro-lotv/app-component-toolbox";
import { grey } from "@faro-lotv/flat-ui";
import type { IElementImg360 } from "@faro-lotv/ielement-types";
import { LotvRenderer } from "@faro-lotv/lotv";
import { Box, Skeleton } from "@mui/material";
import {
  PerformanceMonitor,
  PerspectiveCamera,
  useContextBridge,
} from "@react-three/drei";
import { Canvas } from "@react-three/fiber";
import type { DefaultGLProps } from "@react-three/fiber/dist/declarations/src/core/renderer";
import { isEqual } from "es-toolkit";
import { Suspense, useLayoutEffect, useMemo, useRef, type JSX } from "react";
import { ReactReduxContext } from "react-redux";
import { Matrix4, Quaternion, Vector3 } from "three";

/** Height of the 360 preview canvas in pixels */
const PREVIEW_CANVAS_HEIGHT = 180;

/**
 * Field of view (in degrees) used for the preview camera.
 * A wide FOV is used here so that as much of the panorama as possible is
 * visible at once in the small preview popover.
 */
const PREVIEW_CAMERA_FOV = 110;

type Pano360PreviewCanvasProps = {
  /** The image element to render */
  panoElement: IElementImg360;
};

/**
 * @returns A 360 panorama preview canvas that renders the panoObject
 * with interactive controls for rotating the view.
 */
export function Pano360PreviewCanvas({
  panoElement,
}: Pano360PreviewCanvasProps): JSX.Element {
  // Bridge contexts from parent components to the Canvas
  // Note: AuthContext is NOT bridged here because this component is rendered inside a popover portal.
  // The panoObject must be loaded outside the popover (in ScanPreview) where AuthContext is available.
  const Bridge = useContextBridge(ReactReduxContext, RenderingSettingsContext);

  return (
    <Box
      component="div"
      sx={{
        width: "100%",
        height: PREVIEW_CANVAS_HEIGHT,
        position: "relative",
        cursor: "grab",
        "&:active": {
          cursor: "grabbing",
        },
      }}
    >
      <Suspense
        fallback={
          <Skeleton
            variant="rectangular"
            height="100%"
            sx={{ background: grey[800] }}
          />
        }
      >
        <Canvas gl={createPreviewRenderer} {...DEFAULT_CANVAS_OPTIONS}>
          <Bridge>
            <PerformanceMonitor>
              <SuspenseLoadNotifier withSnapshotRenderer={false}>
                {/* Keyed so switching panos resets the camera and controls instead
                    of inheriting the previous pano's interaction. */}
                <Pano360Scene key={panoElement.id} panoElement={panoElement} />
              </SuspenseLoadNotifier>
            </PerformanceMonitor>
          </Bridge>
        </Canvas>
      </Suspense>
    </Box>
  );
}

// === Helpers ===

/**
 * The heading every preview starts from when the pano has no saved camera view.
 * The popover shows no spatial context, so a fixed world heading (the same one walk
 * mode defaults to) keeps all previews comparable instead of pointing each one
 * wherever that specific pano or trajectory happens to face.
 */
const DEFAULT_CAMERA_ROTATION = new Quaternion().setFromRotationMatrix(
  new Matrix4().lookAt(
    new Vector3(),
    DEFAULT_LOOK_AT_DIR,
    new Vector3(0, 1, 0),
  ),
);

type Pano360PreviewSceneProps = {
  /** The image element to render */
  panoElement: IElementImg360;
};

/**
 * @returns The inner 360 scene that renders the panorama and controls
 */
function Pano360Scene({ panoElement }: Pano360PreviewSceneProps): JSX.Element {
  // Reuse walk-mode pano camera transform logic to match orientation behavior
  const panoTransform = usePanoCameraTransform(panoElement);

  const savedCameraView = useAppSelector(
    selectBestCameraViewForElementInThreeSpace(panoElement.id),
    isEqual,
  );

  // Apply the pano's quaternion to the sphere (same as panorama-renderer.tsx)
  const panoQuaternion = useMemo(
    () => new Quaternion(...panoTransform.quaternion),
    [panoTransform.quaternion],
  );

  // The camera is kept level (up = world Y) so SphereControls' drag axes stay
  // correct, and the panorama sphere is rendered with its world orientation, so
  // the heading only affects where the preview initially looks, never whether
  // the horizon is level.
  const cameraQuaternion = savedCameraView?.rotation ?? DEFAULT_CAMERA_ROTATION;

  // SphereControls latches the camera orientation when it is created and drives the
  // camera from then on, so it has to be re-synced whenever the desired orientation
  // changes after mount (e.g. the saved camera view is only loaded later).
  const controlsRef = useRef<SphereControlsRef>(undefined);
  useLayoutEffect(() => {
    controlsRef.current?.updateFromCamera();
  }, [cameraQuaternion]);

  return (
    <>
      {/* Camera positioned at origin, with initial rotation matching main viewer */}
      <PerspectiveCamera
        makeDefault
        fov={PREVIEW_CAMERA_FOV}
        position={[0, 0, 0]}
        quaternion={cameraQuaternion}
      />

      {/* Render the panorama sphere at origin with its world orientation,
          exactly like the walk mode renderer does. */}
      <PanoPreviewRenderer
        panoElement={panoElement}
        panoQuaternion={panoQuaternion}
      />

      {/* Controls for rotating the camera view inside the pano.
          maxPerspFov is raised to the wide preview FOV so the controls don't
          clamp the camera back down to their default maximum (110°). */}
      <SphereControls
        ref={controlsRef}
        isDefault
        enabled
        maxPerspFov={PREVIEW_CAMERA_FOV}
      />
    </>
  );
}

type PanoPreviewRendererProps = {
  panoElement: IElementImg360;
  panoQuaternion: Quaternion;
};

/**
 * Very simple R3F component to render a panorama image with a given rotation. No LOD tiles fetching happens here.
 * LOD tiles fetching would need a 'useFrame' hook within this component. However, using
 * a 'useFrame' hook here will make the pano object live forever inside an undisposed R3F context.
 *
 * @returns An extremely simple R3F component that renders a panorama image with a given rotation.
 */
function PanoPreviewRenderer({
  panoElement,
  panoQuaternion,
}: PanoPreviewRendererProps): JSX.Element | null {
  const panoObject = useCached3DObjectIfExists(panoElement, "Pano360Scene");

  if (!panoObject) return null;

  return (
    <group quaternion={panoQuaternion}>
      <primitive object={panoObject} dispose={null} />
    </group>
  );
}

/**
 * Creates a custom LotvRenderer for the preview canvas
 *
 * @returns A configured LotvRenderer instance for the preview
 */
function createPreviewRenderer({ canvas }: DefaultGLProps): LotvRenderer {
  const renderer = new LotvRenderer({
    canvas,
    premultipliedAlpha: false,
  });
  renderer.localClippingEnabled = true;
  return renderer;
}
